// Route path parameters type
type RouteParams = {
  id: string;
};

// Base route paths
const BASE_ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGN_UP: '/sign-up',
  DASHBOARD: '/dashboard',
  OVERVIEW: '/overview',
  ICONS: '/icons',
  FORGOT_PASSWORD: '/forgot-password',
  VERIFY_OTP: '/verify-otp',
  ASSETS: '/assets',
  BUDGET: '/budget',
  HISTORY: '/transactions',
  PROFILE: '/profile',
  MINO_TERMS: '/dieu-khoan-mimo',
  DISCOVER: '/discover',
  SETTINGS: '/discover/settings',
  UPGRADE_ACCOUNT: '/discover/upgrade',
  REWARDS: '/discover/rewards',
  CATEGORIES: '/discover/categories',
  CATEGORY_GROUP: '/discover/category-group',
} as const;

// Wallet related routes
const WALLET_ROUTES = {
  LIST: '/assets/wallets',
  CREATE: {
    BASIC: '/assets/wallets/create/basic',
    CREDIT: '/assets/wallets/create/credit',
    DEBIT: '/assets/wallets/create/debit',
    DEBT: '/assets/wallets/create/debt',
    LOAN: '/assets/wallets/create/loan',
    OTHER: '/assets/wallets/create/other',
  },
  EDIT: '/assets/wallets/:id/edit',
  EDIT_BASIC_WALLET: '/assets/wallets/basic/:id/edit',
  EDIT_LOAN_WALLET: '/assets/wallets/loan/:id/edit',
  INSTALLMENT: '/assets/wallets/:id/installments',
  DETAIL: {
    BASIC: '/assets/wallets/basic/:id',
    CREDIT: '/assets/wallets/credit/:id',
    LOAN: '/assets/wallets/loan/:id',
  },
  LOAN_OFFSET: '/assets/wallets/loan/:id/offset',
  STATEMENT: {
    CREDIT: '/assets/wallets/credit/:id/statements/:statementId',
    CREDIT_TRANSACTION: '/assets/wallets/credit/:id/statements/:statementId/transactions/:transactionId',
  },
} as const;

// Transaction related routes
const TRANSACTION_ROUTES = {
  CREATE: {
    BASIC: '/transactions/create/basic',
  },
  EDIT: {
    BASIC: '/transactions/edit/basic/:transactionId',
    LOAN: '/transactions/edit/loan/:transactionId',
  },
  DETAIL: '/transactions/:transactionId',
  BORROWING: {
    DETAIL: '/transactions/borrowing/:transactionId',
    EDIT: '/transactions/borrowing/:transactionId/edit',
  },
  LENDING: {
    DETAIL: '/transactions/lending/:transactionId',
    EDIT: '/transactions/lending/:transactionId/edit',
  },
  REPAYMENT: {
    DETAIL: '/transactions/repayment/:transactionId',
    EDIT: '/transactions/repayment/:transactionId/edit',
  },
  COLLECTING: {
    DETAIL: '/transactions/collecting/:transactionId',
    EDIT: '/transactions/collecting/:transactionId/edit',
  },
  CLEAR_DEBT: {
    DETAIL: '/transactions/clear-debt/:transactionId',
    EDIT: '/transactions/clear-debt/:transactionId/edit',
  }
} as const;

// Route helper function
const createRoute = <T extends string>(path: T) => path;

// Main routes object with type safety
export const ROUTES = {
  ...BASE_ROUTES,
  WALLETS: WALLET_ROUTES.LIST,
  EDIT_WALLET: WALLET_ROUTES.EDIT,
  EDIT_BASIC_WALLET: WALLET_ROUTES.EDIT_BASIC_WALLET,
  EDIT_LOAN_WALLET: WALLET_ROUTES.EDIT_LOAN_WALLET,
  WALLET_INSTALLMENT: WALLET_ROUTES.INSTALLMENT,
  CREATE_BASIC_WALLET: createRoute(WALLET_ROUTES.CREATE.BASIC),
  CREATE_CREDIT_WALLET: createRoute(WALLET_ROUTES.CREATE.CREDIT),
  CREATE_DEBIT_WALLET: createRoute(WALLET_ROUTES.CREATE.DEBIT),
  CREATE_DEBT_WALLET: createRoute(WALLET_ROUTES.CREATE.DEBT),
  CREATE_LOAN_WALLET: createRoute(WALLET_ROUTES.CREATE.LOAN),
  CREATE_OTHER_WALLET: createRoute(WALLET_ROUTES.CREATE.OTHER),
  CREATE_BASIC_TRANSACTION: createRoute(TRANSACTION_ROUTES.CREATE.BASIC),
  EDIT_BASIC_TRANSACTION: createRoute(TRANSACTION_ROUTES.EDIT.BASIC),
  EDIT_LOAN_TRANSACTION: createRoute(TRANSACTION_ROUTES.EDIT.LOAN),
  CREDIT_WALLET_DETAIL: createRoute(WALLET_ROUTES.DETAIL.CREDIT),
  BASIC_WALLET_DETAIL: createRoute(WALLET_ROUTES.DETAIL.BASIC),
  LOAN_WALLET_DETAIL: createRoute(WALLET_ROUTES.DETAIL.LOAN),
  LOAN_WALLET_OFFSET: createRoute(WALLET_ROUTES.LOAN_OFFSET),
  CREDIT_STATEMENT_DETAIL: createRoute(WALLET_ROUTES.STATEMENT.CREDIT),
  CREDIT_TRANSACTION_DETAIL: createRoute(WALLET_ROUTES.STATEMENT.CREDIT_TRANSACTION),
  TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.DETAIL),
  BORROWING_TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.BORROWING.DETAIL),
  BORROWING_TRANSACTION_EDIT: createRoute(TRANSACTION_ROUTES.BORROWING.EDIT),
  REPAYMENT_TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.REPAYMENT.DETAIL),
  REPAYMENT_TRANSACTION_EDIT: createRoute(TRANSACTION_ROUTES.REPAYMENT.EDIT),
  LENDING_TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.LENDING.DETAIL),
  LENDING_TRANSACTION_EDIT: createRoute(TRANSACTION_ROUTES.LENDING.EDIT),
  COLLECTING_TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.COLLECTING.DETAIL),
  COLLECTING_TRANSACTION_EDIT: createRoute(TRANSACTION_ROUTES.COLLECTING.EDIT),
  CLEAR_DEBT_TRANSACTION_DETAIL: createRoute(TRANSACTION_ROUTES.CLEAR_DEBT.DETAIL),
  CLEAR_DEBT_TRANSACTION_EDIT: createRoute(TRANSACTION_ROUTES.CLEAR_DEBT.EDIT),
} as const;

// Type for route paths
export type RoutePath = typeof ROUTES[keyof typeof ROUTES];

// Helper function to generate route with parameters
export const generateRoute = <T extends RoutePath>(
  path: T,
  params?: Partial<RouteParams>
): T => {
  if (!params) return path;

  let result = path as string;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, value);
  }
  return result as T;
};
